import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Euro,
  Briefcase,
  FileText,
  Shield,
  Car,
  Check,
  Minus,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const CommandeViewModal = ({ commande, isOpen, onClose }) => {
  if (!isOpen || !commande) return null;

  const formatDate = (date) => {
    if (!date) return 'Non définie';
    return format(new Date(date), 'dd MMMM yyyy', { locale: fr });
  };

  const formatTime = (time) => {
    if (!time) return 'Non définie';
    return time;
  };

  const getContractTypeLabel = (type) => {
    const types = {
      'CDI': 'Placement CDI',
      'CDD': 'Placement CDD',
      'Intérim': 'Intérim',
      'Vacation': 'Vacation'
    };
    return types[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-amber-50 text-amber-700 border-amber-200',
      'active': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'completed': 'bg-blue-50 text-blue-700 border-blue-200',
      'cancelled': 'bg-red-50 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'En attente',
      'active': 'Active',
      'completed': 'Terminée',
      'cancelled': 'Annulée'
    };
    return labels[status] || status;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 bg-slate-50/50">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">
                Détails de la commande
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                #{commande._id?.slice(-8) || 'N/A'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:shadow-sm rounded-xl transition-all duration-200 text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="px-8 py-6 space-y-8">
              {/* Status et Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Statut
                  </label>
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border ${getStatusColor(commande.status)}`}>
                    {getStatusLabel(commande.status)}
                  </span>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    Type de contrat
                  </label>
                  <div className="flex items-center text-slate-900 font-medium">
                    <Briefcase size={16} className="mr-2 text-slate-400" />
                    {getContractTypeLabel(commande.typeContrat)}
                  </div>
                </div>
              </div>

              {/* Qualification et Quantité */}
              <div className="bg-slate-50/50 rounded-xl p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Qualification
                    </label>
                    <p className="text-slate-900 font-medium">
                      {commande.qualification || 'Non spécifiée'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Quantité
                    </label>
                    <p className="text-slate-900 font-medium">
                      {commande.quantity || 1} personne(s)
                    </p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Calendar size={18} className="text-slate-400" />
                  <h3 className="text-base font-semibold text-slate-900">Dates de mission</h3>
                </div>

                {commande.typeContrat === 'CDI' && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Date de début
                      </label>
                      <p className="text-slate-900 font-medium">{formatDate(commande.startDate)}</p>
                      <p className="text-xs text-slate-500">Placement CDI - Pas de date de fin</p>
                    </div>
                  </div>
                )}

                {commande.typeContrat === 'CDD' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                          Date de début
                        </label>
                        <p className="text-slate-900 font-medium">{formatDate(commande.startDate)}</p>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                          Date de fin
                        </label>
                        <p className="text-slate-900 font-medium">{formatDate(commande.endDate)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {(commande.typeContrat === 'Vacation' || commande.typeContrat === 'Intérim') && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="space-y-3">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Dates sélectionnées
                      </label>
                      {commande.selectedDates && commande.selectedDates.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {commande.selectedDates.map((date, index) => (
                            <span
                              key={index}
                              className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium"
                            >
                              {formatDate(date)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">Aucune date sélectionnée</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Horaires */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Clock size={18} className="text-slate-400" />
                  <h3 className="text-base font-semibold text-slate-900">Horaires de travail</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Heure de début
                      </label>
                      <p className="text-slate-900 font-medium">{formatTime(commande.startHour)}</p>
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Heure de fin
                      </label>
                      <p className="text-slate-900 font-medium">{formatTime(commande.endHour)}</p>
                    </div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Heures hebdo
                      </label>
                      <p className="text-slate-900 font-medium">{commande.nbHeureHebdo || 'Non spécifié'}h</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Jours travaillés */}
              {commande.workedDays && commande.workedDays.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-slate-900">Jours travaillés</h3>
                  <div className="flex flex-wrap gap-2">
                    {commande.workedDays.map((day, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium"
                      >
                        {day}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Informations générales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Adresse */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <MapPin size={18} className="text-slate-400" />
                    <h3 className="text-base font-semibold text-slate-900">Lieu de mission</h3>
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="space-y-2">
                      <p className="text-slate-900 font-medium">
                        {commande.address || 'Adresse non spécifiée'}
                      </p>
                      {(commande.postCode || commande.town) && (
                        <p className="text-slate-500 text-sm">
                          {commande.postCode} {commande.town}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tarif et Véhicule */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Euro size={18} className="text-slate-400" />
                    <h3 className="text-base font-semibold text-slate-900">Tarification</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                          Tarif horaire
                        </label>
                        <p className="text-slate-900 font-semibold text-lg">
                          {commande.hourlyRate || '0'}€/heure
                        </p>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Car size={16} className="text-slate-400" />
                          <span className="text-slate-900 font-medium">Véhicule</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {commande.isVehicule ? (
                            <>
                              <Check size={16} className="text-emerald-600" />
                              <span className="text-emerald-700 text-sm font-medium">Requis</span>
                            </>
                          ) : (
                            <>
                              <Minus size={16} className="text-slate-400" />
                              <span className="text-slate-500 text-sm">Non requis</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descriptions */}
              {(commande.task || commande.justificatif || commande.consigne) && (
                <div className="space-y-6">
                  {commande.task && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <FileText size={18} className="text-slate-400" />
                        <h3 className="text-base font-semibold text-slate-900">Description des tâches</h3>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {commande.task}
                        </p>
                      </div>
                    </div>
                  )}

                  {commande.justificatif && (
                    <div className="space-y-4">
                      <h3 className="text-base font-semibold text-slate-900">Justificatif</h3>
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {commande.justificatif}
                        </p>
                      </div>
                    </div>
                  )}

                  {commande.consigne && (
                    <div className="space-y-4">
                      <h3 className="text-base font-semibold text-slate-900">Consignes particulières</h3>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-amber-900 leading-relaxed whitespace-pre-wrap">
                          {commande.consigne}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* EPIs et Primes */}
              {((commande.epis && commande.epis.length > 0) || (commande.primes && commande.primes.length > 0)) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* EPIs */}
                  {commande.epis && commande.epis.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Shield size={18} className="text-slate-400" />
                        <h3 className="text-base font-semibold text-slate-900">EPI requis</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {commande.epis.map((epi, index) => (
                          <span
                            key={index}
                            className="px-3 py-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg text-sm font-medium"
                          >
                            {epi}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Primes */}
                  {commande.primes && commande.primes.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Euro size={18} className="text-slate-400" />
                        <h3 className="text-base font-semibold text-slate-900">Primes</h3>
                      </div>
                      <div className="space-y-2">
                        {commande.primes.map((prime, index) => (
                          <div key={index} className="bg-white border border-slate-200 rounded-xl p-3">
                            <div className="flex justify-between items-center">
                              <span className="text-slate-900 font-medium">
                                {typeof prime === 'object' ?
                                  String(prime.prime || prime.primeType || `Prime ${index + 1}`) :
                                  `Prime ${index + 1}`
                                }
                              </span>
                              <span className="font-semibold text-slate-900">
                                {typeof prime === 'object' ?
                                  String(prime.taux || prime.amount || '0') :
                                  String(prime)
                                }€
                              </span>
                            </div>
                            {typeof prime === 'object' && prime.primeType && prime.primeType !== prime.prime && (
                              <div className="text-xs text-slate-500 mt-1">
                                {prime.primeType}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Informations système */}
              <div className="border-t border-slate-100 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Créée le
                    </label>
                    <p className="text-slate-700">
                      {commande.createdAt ? formatDate(commande.createdAt) : 'Non disponible'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Modifiée le
                    </label>
                    <p className="text-slate-700">
                      {commande.updatedAt ? formatDate(commande.updatedAt) : 'Non disponible'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end px-8 py-6 border-t border-slate-100 bg-slate-50/50">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all duration-200 font-medium"
            >
              Fermer
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CommandeViewModal;
